import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Buildings, UploadSimple, Trash, FloppyDisk, ImagesSquare } from "@phosphor-icons/react";

export default function StudioSettings() {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const { data } = await api.get("/studio-settings");
    setSettings(data);
  };
  useEffect(() => { load().catch(() => toast.error("Errore caricamento")); }, []);

  const setV = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/studio-settings", {
        studio_name: settings.studio_name || null,
        address: settings.address || null,
        phone: settings.phone || null,
        email: settings.email || null,
        website: settings.website || null,
        logo_url: settings.logo_url || null,
        logo_public_id: settings.logo_public_id || null,
      });
      setSettings(data);
      toast.success("Impostazioni salvate");
    } catch (e) { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { data: sig } = await api.get("/cloudinary/signature?folder=studio");
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);
      const res = await fetch(`https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`, {
        method: "POST", body: form,
      });
      const uploaded = await res.json();
      if (!uploaded.secure_url) throw new Error(uploaded?.error?.message || "Upload failed");
      setSettings((s) => ({ ...s, logo_url: uploaded.secure_url, logo_public_id: uploaded.public_id }));
      toast.success("Logo caricato — ricordati di salvare");
    } catch (err) {
      toast.error("Errore upload: " + (err.message || ""));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeLogo = () => {
    setSettings((s) => ({ ...s, logo_url: null, logo_public_id: null }));
  };

  if (!settings) return <div className="text-sm">Caricamento…</div>;

  return (
    <div data-testid="studio-settings-page">
      <div className="label-tiny">Personalizzazione</div>
      <h1 className="font-serif text-5xl tracking-tight mt-2">Studio</h1>
      <p className="mt-3 text-sm text-[color:var(--text-secondary)] max-w-lg">
        Nome, contatti e logo dello studio: verranno stampati nell'intestazione dei PDF esportati.
      </p>

      <div className="mt-8 grid lg:grid-cols-[minmax(0,1fr)_360px] gap-8">
        {/* Text fields */}
        <div className="rounded-2xl border border-[color:var(--border)] p-6 bg-[color:var(--bg-primary)] space-y-5">
          <div className="grid md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <Label className="label-tiny">Nome studio</Label>
              <Input
                data-testid="studio-name"
                value={settings.studio_name || ""}
                onChange={(e) => setV("studio_name", e.target.value)}
                placeholder="es. Studio Nutrizionale Rossi"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="label-tiny">Indirizzo</Label>
              <Input
                data-testid="studio-address"
                value={settings.address || ""}
                onChange={(e) => setV("address", e.target.value)}
                placeholder="Via Roma 12, 20100 Milano"
              />
            </div>
            <div>
              <Label className="label-tiny">Telefono</Label>
              <Input
                data-testid="studio-phone"
                value={settings.phone || ""}
                onChange={(e) => setV("phone", e.target.value)}
                placeholder="+39 333 1234567"
              />
            </div>
            <div>
              <Label className="label-tiny">Email</Label>
              <Input
                data-testid="studio-email"
                value={settings.email || ""}
                onChange={(e) => setV("email", e.target.value)}
                placeholder="info@studio.it"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="label-tiny">Sito web</Label>
              <Input
                data-testid="studio-website"
                value={settings.website || ""}
                onChange={(e) => setV("website", e.target.value)}
                placeholder="www.studio.it"
              />
            </div>
          </div>

          <Button
            data-testid="save-studio-settings-btn"
            onClick={save}
            disabled={saving}
            className="rounded-full bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white"
          >
            <FloppyDisk size={14} weight="bold" className="mr-2" />
            {saving ? "Salvo…" : "Salva impostazioni"}
          </Button>
        </div>

        {/* Logo panel */}
        <div className="rounded-2xl border border-[color:var(--border)] p-6 bg-[color:var(--bg-primary)]">
          <div className="label-tiny inline-flex items-center gap-1.5">
            <ImagesSquare size={14} weight="duotone" style={{ color: "var(--accent)" }} />
            Logo studio
          </div>

          <div
            className="mt-4 aspect-square w-full rounded-xl border border-dashed border-[color:var(--border)] flex items-center justify-center overflow-hidden"
            style={{ background: "var(--bg-secondary)" }}
          >
            {settings.logo_url ? (
              <img
                data-testid="studio-logo-preview"
                src={settings.logo_url}
                alt="Logo"
                className="max-w-full max-h-full object-contain"
              />
            ) : (
              <div className="flex flex-col items-center gap-2 text-[color:var(--text-secondary)] text-xs">
                <Buildings size={30} weight="duotone" />
                Nessun logo caricato
              </div>
            )}
          </div>

          <div className="mt-4 space-y-2">
            <label className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--border)] px-4 py-2 text-sm hover:border-[color:var(--text-primary)] cursor-pointer transition-colors">
              <UploadSimple size={14} weight="bold" />
              {uploading ? "Caricamento…" : (settings.logo_url ? "Sostituisci logo" : "Carica logo")}
              <input
                data-testid="studio-logo-input"
                type="file"
                accept="image/*"
                onChange={uploadLogo}
                className="hidden"
                disabled={uploading}
              />
            </label>
            {settings.logo_url && (
              <button
                type="button"
                data-testid="remove-studio-logo-btn"
                onClick={removeLogo}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[color:var(--border)] px-4 py-2 text-xs text-red-700 hover:bg-red-50 hover:border-red-300 transition-colors"
              >
                <Trash size={13} weight="bold" /> Rimuovi logo
              </button>
            )}
          </div>

          <p className="mt-4 text-[10px] leading-relaxed text-[color:var(--text-secondary)] italic">
            Preferisci un file quadrato con sfondo trasparente (PNG). Sarà stampato nel PDF a circa 22×22 mm.
          </p>
        </div>
      </div>
    </div>
  );
}
